import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import { tokens } from "../tokens";
import { portalFooterContent } from "./portal-footer-content";

/**
 * Shared portal footer (ADR-20261013-1z2y): a "Legal" nav of four FRP links,
 * pipe separators and the copyright line. Rendered by `PortalLayout`, not
 * exported. External links open in a new tab and say so in their accessible
 * name and native title (ADR-20261013-w8g2); there is no custom tooltip.
 * Static: no network, no user data. The year is read at render time.
 */
export function PortalFooter() {
  const { navAccessibleName, links, separator, newTabSuffix, copyright } = portalFooterContent;
  const year = new Date().getFullYear();
  // ADR-20261102-h2fd (AC-14): H2's PortalFooter -- links and dividers as one wrapping row, 10px
  // apart (8px on a phone), each link 15px with 4px above and below; the copyright line beneath.
  const text = { fontSize: 15, lineHeight: 1.5 } as const;
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: tokens.colour.neutral.white,
        borderTop: `${tokens.interactionState.border.hairlineWidth}px solid ${tokens.colour.neutral.borderSubtle}`,
        padding: {
          xs: `${tokens.spacing.lg}px ${tokens.spacing.md}px`,
          sm: `${tokens.spacing.lg}px ${tokens.spacing.xl}px`,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: tokens.layout.footerContentMax,
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: `${tokens.spacing.sm}px`,
        }}
      >
        <Box
          component="nav"
          aria-label={navAccessibleName}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            columnGap: { xs: "8px", sm: "10px" },
            rowGap: `${tokens.spacing.xxs}px`,
          }}
        >
          {links.flatMap((link, index) => [
            ...(index > 0
              ? [
                  <Box key={`${link.href}-separator`} component="span" aria-hidden="true" sx={{ color: tokens.colour.brand[500], fontSize: 15 }}>
                    {separator}
                  </Box>,
                ]
              : []),
            <MuiLink
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${link.text}${newTabSuffix}`}
              title={`${link.text}${newTabSuffix}`}
              sx={{
                ...text,
                color: tokens.colour.brand[700],
                textDecoration: "underline",
                padding: `${tokens.spacing.xxs}px 0`,
                display: "inline-block",
              }}
            >
              {link.text}
            </MuiLink>,
          ])}
        </Box>
        <Box component="p" sx={{ ...text, margin: 0, color: tokens.colour.brand[500] }}>
          {copyright(year)}
        </Box>
      </Box>
    </Box>
  );
}
